import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosResponse } from 'axios';

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const CSRF_COOKIE_NAME = 'XSRF-TOKEN';
export const csrfTokens = new Map<string, string>();
export const csrfFetches = new Map<string, Promise<string>>();

function normalizePath(path: string | undefined): string {
  if (!path) return '';
  return path.startsWith('/') ? path : `/${path}`;
}

function extractCsrfToken(data: unknown): string | null {
  if (typeof data === 'string') {
    const token = data.trim();
    return token.length ? token : null;
  }

  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  const directCandidates = [record.token, record.csrfToken, record.xsrfToken, record.csrf];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  const nestedCandidates = [record.result, record.data];
  for (const nested of nestedCandidates) {
    const token = extractCsrfToken(nested);
    if (token) return token;
  }

  return null;
}

function extractCookieValue(cookieName: string): string | null {
  let cookieString = '';
  /* istanbul ignore next: jsdom-based test runs do not reliably exercise document absence */
  if (typeof document !== 'undefined') cookieString = document.cookie;

  if (!cookieString) return null;

  const cookies = cookieString.split(';');
  for (const cookie of cookies) {
    const [rawName, ...rawValueParts] = cookie.trim().split('=');
    if (rawName !== cookieName) continue;

    const rawValue = rawValueParts.join('=').trim();
    if (!rawValue) return null;

    return decodeURIComponent(rawValue);
  }

  return null;
}

function resolveCsrfToken(data: unknown): string | null {
  return extractCookieValue(CSRF_COOKIE_NAME) || extractCsrfToken(data);
}

function isMutatingMethod(method?: string): boolean {
  return method !== undefined && MUTATING_METHODS.has(method.toLowerCase());
}

function isCsrfResponse(url: string | undefined, csrfPath: string): boolean {
  return normalizePath(url).endsWith(normalizePath(csrfPath));
}

export function createCsrfAwareAxiosInstance(baseURL: string, csrfPath: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 30000,
    withCredentials: true,
    // We set the header ourselves so we can prefer the raw XSRF-TOKEN cookie
    // while still supporting backends that only expose the token in /csrf JSON.
    withXSRFToken: false,
  });

  const loadCsrfToken = async (): Promise<string> => {
    const cachedToken = csrfTokens.get(csrfPath);
    if (cachedToken) return cachedToken;

    const pendingFetch = csrfFetches.get(csrfPath);
    if (pendingFetch) return pendingFetch;

    const fetchPromise = (async () => {
      const response = await instance.get<unknown>(csrfPath, {
        validateStatus: () => true,
      });

      const token = resolveCsrfToken(response.data);
      if (!token) {
        throw new Error(`CSRF token missing in response from ${csrfPath}`);
      }

      csrfTokens.set(csrfPath, token);
      return token;
    })().finally(() => {
      csrfFetches.delete(csrfPath);
    });

    csrfFetches.set(csrfPath, fetchPromise);
    return fetchPromise;
  };

  const clearCsrfToken = () => {
    csrfTokens.delete(csrfPath);
  };

  instance.interceptors.request.use(async (config) => {
    const method = config.method ?? 'get';
    if (isMutatingMethod(method)) {
      const token = await loadCsrfToken();
      const headers = AxiosHeaders.from(config.headers);
      headers.set('X-XSRF-TOKEN', token);
      config.headers = headers;
    }

    config.withCredentials = true;
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse<unknown>) => {
      if (isCsrfResponse(response.config.url, csrfPath)) {
        const token = resolveCsrfToken(response.data);
        if (token) {
          csrfTokens.set(csrfPath, token);
        }
      }

      if (normalizePath(response.config.url).endsWith('/logout')) {
        clearCsrfToken();
      }

      return response;
    },
    (error: AxiosError) => {
      if (
        axios.isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        clearCsrfToken();
      }
      return Promise.reject(error);
    }
  );

  return instance;
}
